/**
 * Cache keys for SolidStart query() functions
 * 
 * Centralized cache key management enables:
 * - Consistent naming across queries
 * - Proper cache invalidation in actions via reload()
 * - Type-safe key references
 */

export const CACHE_KEYS = {
  // Inbox
  INBOX_VIDEOS: "inbox-videos",
  INBOX_STATS: "inbox-stats",
  
  // Videos
  VIDEOS: "videos",
  VIDEO: (id: string) => `video-${id}` as const,
  VIDEO_TRANSCRIPT: (id: string) => `video-transcript-${id}` as const,
  VIDEO_VISUALS: (id: string) => `video-visuals-${id}` as const,
  
  // Channels/Sources
  CHANNELS: "channels",
  CHANNEL: (id: string) => `channel-${id}` as const,
  
  // Credibility (v4.1)
  CREDIBILITY_SCORE: (channelId: string) => `credibility-${channelId}` as const,
  CREDIBILITY_LEADERBOARD: "credibility-leaderboard",
  CREDIBILITY_HISTORY: (channelId: string) => `credibility-history-${channelId}` as const,
  CREDIBILITY_OVERRIDE: (channelId: string) => `credibility-override-${channelId}` as const,
  TOPIC_ACCURACY: (channelId: string) => `topic-accuracy-${channelId}` as const,
  
  // Topics
  TOPICS: "topics",
  TOPIC: (id: string) => `topic-${id}` as const,
  TRENDING_TOPICS: "trending-topics",
  
  // Topic Discovery (v4.2)
  DISCOVERED_VIDEOS: (topicId: string) => `discovered-videos-${topicId}` as const,
  RECOMMENDED_CHANNELS: "recommended-channels",
  BLOCKED_CHANNELS: "blocked-channels",
  
  // Predictions
  PREDICTIONS: "predictions",
  PREDICTION: (id: string) => `prediction-${id}` as const,
  
  // Dossiers
  DOSSIERS: "dossiers",
  DOSSIER: (id: string) => `dossier-${id}` as const,
  DOSSIER_TIMELINE: (id: string) => `dossier-timeline-${id}` as const,
  DOSSIER_RELATIONSHIPS: (id: string) => `dossier-relationships-${id}` as const,
  
  // Analysis
  ANALYSIS_RUNS: "analysis-runs",
  ANALYSIS_RUN: (id: string) => `analysis-run-${id}` as const,
  ANALYSIS_TEMPLATES: "analysis-templates",
  
  // Conversations/Chat
  CONVERSATIONS: "conversations",
  CONVERSATION: (id: string) => `conversation-${id}` as const,
  
  // Reports
  REPORTS: "reports",
  REPORT: (id: string) => `report-${id}` as const,
  
  // Notifications
  NOTIFICATIONS: "notifications",
  UNREAD_COUNT: "unread-notification-count",
  
  // Briefing
  TODAYS_BRIEFING: "todays-briefing",
  
  // Search
  SEARCH_VIDEOS: (query: string) => `search-videos-${query}` as const,
  SEARCH_CHUNKS: (query: string) => `search-chunks-${query}` as const,
  SAVED_SEARCHES: "saved-searches",
  SEARCH_ALERT_HISTORY: (searchId: string) => `search-alert-history-${searchId}` as const,
  
  // User Settings
  USER_SETTINGS: "user-settings",
  USER_APPEARANCE: "user-appearance",
  USER_NOTIFICATIONS: "user-notifications",
  AUTOMATION_RULES: "automation-rules",
  AUTOMATION_LOG: "automation-log",
  STORAGE_STATS: "storage-stats",
  
  // Entities (v4.3)
  ENTITIES: "entities",
  ENTITY: (id: string) => `entity-${id}` as const,
  VIDEO_ENTITIES: (videoId: string) => `video-entities-${videoId}` as const,
  ENTITY_MENTIONS: (entityId: string) => `entity-mentions-${entityId}` as const,
  ENTITY_SEARCH: (query: string) => `entity-search-${query}` as const,
  SUGGESTED_TAGS: (videoId: string) => `suggested-tags-${videoId}` as const,
  
  // PWA / Push Notifications (v4.3)
  PUSH_SUBSCRIPTIONS: "push-subscriptions",
  OFFLINE_ACTIONS: "offline-actions",
  PUSH_NOTIFICATION_LOG: "push-notification-log",
  
  // Scheduled Analyses (v4.4)
  SCHEDULED_ANALYSES: "scheduled-analyses",
  SCHEDULED_ANALYSIS: (id: string) => `scheduled-analysis-${id}` as const,
  SCHEDULED_ANALYSIS_RUNS: (scheduleId: string) => `scheduled-analysis-runs-${scheduleId}` as const,
  
  // Report Comparison (v4.4)
  REPORT_COMPARISON: (reportId1: string, reportId2: string) => `report-comparison-${reportId1}-${reportId2}` as const,
  COMPARABLE_REPORTS: (reportId: string) => `comparable-reports-${reportId}` as const,
} as const;

/**
 * Helper to get invalidation keys for related entities
 * Used in actions to determine which queries to revalidate
 */
export const INVALIDATION_GROUPS = {
  // When a video is triaged, invalidate inbox and video lists
  VIDEO_TRIAGE: [
    CACHE_KEYS.INBOX_VIDEOS,
    CACHE_KEYS.INBOX_STATS,
    CACHE_KEYS.VIDEOS,
  ],
  
  // When a prediction is updated
  PREDICTION_UPDATE: [
    CACHE_KEYS.PREDICTIONS,
    CACHE_KEYS.TODAYS_BRIEFING,
  ],
  
  // When a dossier is modified
  DOSSIER_UPDATE: [
    CACHE_KEYS.DOSSIERS,
  ],
  
  // When notifications change
  NOTIFICATION_UPDATE: [
    CACHE_KEYS.NOTIFICATIONS,
    CACHE_KEYS.UNREAD_COUNT,
  ],
  
  // When channels change
  CHANNEL_UPDATE: [
    CACHE_KEYS.CHANNELS,
  ],
  
  // When credibility changes
  CREDIBILITY_UPDATE: [
    CACHE_KEYS.CHANNELS,
    CACHE_KEYS.CREDIBILITY_LEADERBOARD,
  ],
  
  // When saved searches change
  SAVED_SEARCH_UPDATE: [
    CACHE_KEYS.SAVED_SEARCHES,
  ],
  
  // When topics change
  TOPIC_UPDATE: [
    CACHE_KEYS.TOPICS,
    CACHE_KEYS.TRENDING_TOPICS,
  ],
  
  // When topic discovery changes (v4.2)
  DISCOVERY_UPDATE: [
    CACHE_KEYS.TOPICS,
    CACHE_KEYS.RECOMMENDED_CHANNELS,
    CACHE_KEYS.BLOCKED_CHANNELS,
  ],
  
  // When dossier timeline/relationships change (v4.2)
  DOSSIER_TIMELINE_UPDATE: [
    CACHE_KEYS.DOSSIERS,
  ],
  
  // When reports change
  REPORT_UPDATE: [
    CACHE_KEYS.REPORTS,
  ],
  
  // When conversations change
  CONVERSATION_UPDATE: [
    CACHE_KEYS.CONVERSATIONS,
  ],
  
  // When analysis runs complete (includes duplicate/rerun)
  ANALYSIS_UPDATE: [
    CACHE_KEYS.ANALYSIS_RUNS,
  ],
  
  // When user settings change
  SETTINGS_UPDATE: [
    CACHE_KEYS.USER_SETTINGS,
    CACHE_KEYS.USER_APPEARANCE,
    CACHE_KEYS.USER_NOTIFICATIONS,
  ],
  
  // When automation rules change
  AUTOMATION_UPDATE: [
    CACHE_KEYS.AUTOMATION_RULES,
    CACHE_KEYS.AUTOMATION_LOG,
  ],
  
  // When entities change (v4.3)
  ENTITY_UPDATE: [
    CACHE_KEYS.ENTITIES,
  ],
  
  // When suggested tags change (v4.3)
  SUGGESTED_TAGS_UPDATE: [
    CACHE_KEYS.VIDEOS,
  ],
  
  // When push subscriptions change (v4.3)
  PUSH_SUBSCRIPTION_UPDATE: [
    CACHE_KEYS.PUSH_SUBSCRIPTIONS,
  ],
  
  // When offline actions sync (v4.3)
  OFFLINE_SYNC_UPDATE: [
    CACHE_KEYS.OFFLINE_ACTIONS,
    CACHE_KEYS.INBOX_VIDEOS,
    CACHE_KEYS.DOSSIERS,
  ],
  
  // When scheduled analyses change (v4.4)
  SCHEDULED_ANALYSIS_UPDATE: [
    CACHE_KEYS.SCHEDULED_ANALYSES,
  ],
  
  // When a scheduled analysis run completes (v4.4)
  SCHEDULED_RUN_UPDATE: [
    CACHE_KEYS.SCHEDULED_ANALYSES,
    CACHE_KEYS.ANALYSIS_RUNS,
  ],
  
  // When report comparisons are generated (v4.4)
  REPORT_COMPARISON_UPDATE: [
    CACHE_KEYS.REPORTS,
  ],
} as const;

export type CacheKey = typeof CACHE_KEYS[keyof typeof CACHE_KEYS];
